import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useState } from "react";
import styles from "../stylesheets/components/highlights-layout-4.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  HighlightsAspectRatio,
  HighlightsDisplayType,
  HighlightsItem,
  Language,
  TitleAndSubtitleTextAlign,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import CroppedImage from "./CroppedImage.js";
import HighlightHeading from "./HighlightHeading.js";
import ModuleHeadings from "./ModuleHeadings.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  isFirstOnPage: boolean;
  scheme: ColorScheme;
  items: HighlightsItem[];
  queries: ContainerQueries;
  displayType: HighlightsDisplayType;
  activeModuleId: string | undefined;
  isPreview: boolean;
  transparentSliderArrowBackground: boolean;
  languageId: Language;
}

const HighlightsLayout4: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  items,
  queries,
  displayType,
  activeModuleId,
  isPreview,
  transparentSliderArrowBackground,
  languageId,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const itemsCount = items.length;
  const isSliderEnabled =
    displayType === "slider" && getShouldEnableSlider(queries, itemsCount);
  const hasHighlightsTitle = !!title;

  const highlights = items.map(
    ({ id, title, subtitle, picture, imageDetail, buttons }, index) => (
      <article key={id} className={styles.HighlightsLayout4__Item}>
        <div className={styles.HighlightsLayout4__ItemPicture}>
          <CroppedImage
            aspectRatio={getAspectRatio(queries)}
            picture={picture}
            width={1280}
            detail={imageDetail}
            sizes="(min-width: 640px) 50vw, (min-width: 1024px) 33vw, (min-width: 1600px) 500px, 100vw"
          />
          {buttons[0]}
        </div>

        <HighlightHeading
          className={styles.HighlightsLayout4__ItemContent}
          title={{
            value: title,
            children: title,
            className: ClassNames(
              styles.HighlightsLayout4__ItemTitle,
              css({ color: scheme.main.title }),
            ),
          }}
          subtitle={{
            value: subtitle,
            className: ClassNames(
              styles.HighlightsLayout4__ItemSubtitle,
              css({ color: scheme.main.title }),
            ),
          }}
          highlightIndex={index}
          hasHighlightsTitle={hasHighlightsTitle}
          isFirstOnPage={isFirstOnPage}
        />
      </article>
    ),
  );

  return (
    <div
      className={ClassNames(
        styles.HighlightsLayout4,
        css({ background: scheme.main.separator, color: scheme.main.text }),
      )}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
        <div
          className={ClassNames(styles.HighlightsLayout4__Items, {
            [styles["HighlightsLayout4__Items--count-1"]]: itemsCount === 1,
            [styles["HighlightsLayout4__Items--count-2"]]: itemsCount === 2,
          })}
        >
          {isSliderEnabled && (
            <Slider
              containerClassName={styles.HighlightsLayout4__SliderContainer}
              slideClassName={styles.HighlightsLayout4__Slide}
              isPreview={isPreview}
              sliderEffect="slide"
              onSliderLoaded={setSlider}
              slidesToShow={getSlidesToShow(queries)}
              activePreviewSlideId={activeModuleId}
              slideIds={items.map(({ id }) => id)}
            >
              {highlights}
            </Slider>
          )}
          {!isSliderEnabled && (
            <div className={styles.HighlightsLayout4__List}>{highlights}</div>
          )}
          {isSliderEnabled && (
            <div>
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout4__SliderArrow,
                  styles["HighlightsLayout4__SliderArrow--left"],
                )}
                iconClassName={styles.HighlightsLayout4__SliderArrowIcon}
                direction="left"
                onClick={() => slider?.scrollPrev()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout4__SliderArrow,
                  styles["HighlightsLayout4__SliderArrow--right"],
                )}
                iconClassName={styles.HighlightsLayout4__SliderArrowIcon}
                direction="right"
                onClick={() => slider?.scrollNext()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getShouldEnableSlider = (
  queries: ContainerQueries,
  highlightsCount: number,
) => {
  const isXLarge = queries["Query--xlarge"];
  const isLarge = queries["Query--large"];

  return (
    (isXLarge && highlightsCount > 3) ||
    (!isXLarge && isLarge && highlightsCount > 2) ||
    (!isLarge && highlightsCount > 1)
  );
};

const getSlidesToShow = (queries: ContainerQueries) =>
  queries["Query--xlarge"] ? 3 : queries["Query--large"] ? 2 : 1;

const getAspectRatio = (queries: ContainerQueries): HighlightsAspectRatio => {
  if (queries["Query--xlarge"]) return 0.8333;
  if (queries["Query--medium"]) return 1.3333;
  return 0.8333;
};

export default HighlightsLayout4;
